import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type Block =
  | { type: 'h'; level: 2 | 3 | 4; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'quote'; text: string }
  | { type: 'hr' }
  | { type: 'code'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'img'; alt: string; src: string };

/** Renders a KI answer. Model HTML is never injected. A plain paragraph with catalogue facts becomes a table and a list. */
export function AiMarkdown({ text, className }: { text: string; className?: string }) {
  const blocks = parseBlocks(structurePlainAiAnswer(text.trim()));
  if (blocks.length === 0) {
    return null;
  }
  return (
    <div className={cn('space-y-2 text-sm leading-relaxed break-words text-foreground', className)}>
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
}

function renderBlock(block: Block, index: number): ReactNode {
  if (block.type === 'h') {
    const Tag = block.level === 2 ? 'h2' : block.level === 3 ? 'h3' : 'h4';
    const size = block.level === 2 ? 'text-lg' : block.level === 3 ? 'text-base' : 'text-sm';
    return (
      <Tag key={index} className={`${size} font-semibold text-primary`}>
        {renderInline(block.text)}
      </Tag>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul key={index} className="list-disc space-y-1 pl-5">
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{renderInline(item)}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'ol') {
    return (
      <ol key={index} className="list-decimal space-y-1 pl-5">
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{renderInline(item)}</li>
        ))}
      </ol>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote key={index} className="border-l-2 border-border pl-3 text-muted-foreground">
        {renderInline(block.text)}
      </blockquote>
    );
  }
  if (block.type === 'hr') {
    return <hr key={index} className="border-border" />;
  }
  if (block.type === 'code') {
    return (
      <pre key={index} className="overflow-x-auto rounded-md bg-muted px-3 py-2">
        <code>{block.text}</code>
      </pre>
    );
  }
  if (block.type === 'table') {
    return (
      <div key={index} className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              {block.headers.map((header, headerIndex) => (
                <th
                  key={headerIndex}
                  className="border border-border bg-muted px-2 py-1 font-medium"
                >
                  {renderInline(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {block.headers.map((_, cellIndex) => (
                  <td key={cellIndex} className="border border-border px-2 py-1">
                    {renderInline(row[cellIndex] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === 'img') {
    const src = safeHttp(block.src);
    if (!src) {
      return block.alt ? <p key={index}>{block.alt}</p> : null;
    }
    return <img key={index} src={src} alt={block.alt} className="max-w-full rounded-md" />;
  }
  return <p key={index}>{renderInline(block.text)}</p>;
}

function structurePlainAiAnswer(text: string): string {
  if (hasBlockMarkdown(text)) {
    return text;
  }
  const sentences = text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+(?=\p{Lu})/u)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0);
  const rows: [string, string][] = [];
  const labelled: LabelledValues[] = [];
  const lists: { title: string; items: string[] }[] = [];
  const prose: string[] = [];
  let advantages: string[] = [];
  for (const sentence of sentences) {
    const fact = factRow(sentence);
    if (fact) {
      rows.push(fact);
      continue;
    }
    const labelledValues = labelledValueRow(sentence);
    if (labelledValues) {
      labelled.push(labelledValues);
      continue;
    }
    const listed = advantageItems(sentence);
    if (listed.length >= 2) {
      advantages = listed;
      continue;
    }
    const steps = stepList(sentence);
    if (steps) {
      lists.push(steps);
      continue;
    }
    const section = sectionList(sentence);
    if (section) {
      lists.push(section);
      continue;
    }
    prose.push(sentence);
  }
  if (rows.length === 0 && labelled.length === 0 && advantages.length < 2 && lists.length === 0) {
    return sentences.length < 2 ? text : sentences.join('\n\n');
  }
  const parts: string[] = [];
  if (prose.length > 0) {
    parts.push(prose.join('\n\n'));
  }
  if (rows.length > 0) {
    parts.push(markdownTable(['Angabe', 'Wert'], rows));
  }
  if (labelled.length > 0) {
    parts.push(labelledTable(labelled));
  }
  for (const list of lists) {
    parts.push(markdownList(list.title, list.items, list.title === 'Vorteile' ? '##' : '#'));
  }
  if (advantages.length >= 2) {
    parts.push(markdownList('Vorteile', advantages));
  }
  return parts.join('\n\n');
}

type LabelledValues = { subject: string; kind: string; value: string };

function labelledTable(rows: LabelledValues[]): string {
  const kinds = new Set(rows.map((row) => row.kind.toLocaleLowerCase('de')));
  if (kinds.size === 1) {
    const kind = rows[0]?.kind ?? 'Angabe';
    return [
      `# ${kind}`,
      '',
      markdownTable(
        ['Gegenstand', kind],
        rows.map((row) => [row.subject, row.value]),
      ),
    ].join('\n');
  }
  return [
    '# Angaben',
    '',
    markdownTable(
      ['Gegenstand', 'Angabe', 'Wert'],
      rows.map((row) => [row.subject, row.kind, row.value]),
    ),
  ].join('\n');
}

function markdownTable(headers: string[], rows: string[][]): string {
  return [
    `| ${headers.map(tableCell).join(' | ')} |`,
    `| ${headers.map(() => '---').join(' | ')} |`,
    ...rows.map(
      (row) => `| ${headers.map((_, index) => tableCell(row[index] ?? '')).join(' | ')} |`,
    ),
  ].join('\n');
}

function markdownList(title: string, items: string[], mark = '##'): string {
  return [`${mark} ${title}`, '', ...items.map((item) => `- ${item}`)].join('\n');
}

function tableCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\s+/g, ' ').trim();
}

/** „Für die Kappen gibt es folgende Artikelnummern: 1, 2 und 3.“ */
function labelledValueRow(sentence: string): LabelledValues | null {
  const match =
    /^(?:Für (?:die|den|das)\s+)?(.+?)\s+gibt es(?:\s+folgende)?\s+([^:]{2,60}):\s+(.+)$/iu.exec(
      sentence,
    );
  if (!match?.[1] || !match[2] || !match[3]) {
    return null;
  }
  const subject = match[1].trim();
  const kind = match[2].trim();
  const value = match[3].replace(/[.!?]+$/u, '').trim();
  if (subject.length === 0 || subject.length > 80 || value.length === 0) {
    return null;
  }
  return { subject, kind, value };
}

/** „Gefahren: Brand, Explosion und Druckanstieg.“ */
function sectionList(sentence: string): { title: string; items: string[] } | null {
  const match =
    /^(?:Die |Der |Das )?(Gefahren|Schutzmaßnahmen|Verhaltensregeln|Maßnahmen|Anwendung|Anwendungen|Hinweise|Vorteile|Schritte)\b\s*(?::|sind|ist)\s+(.+)$/iu.exec(
      sentence,
    );
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const items = splitPlainItems(match[2]);
  if (items.length < 2 || items.some((item) => item.length > 80 || /^\d{1,2}$/u.test(item))) {
    return null;
  }
  return { title: match[1].trim(), items };
}

/** „Die Schritte sind: öffnen, prüfen und schließen.“ */
function stepList(sentence: string): { title: string; items: string[] } | null {
  const match = /^(?:Die |Der |Das )?(.{3,40}?)\s+(?:sind|ist):\s+(.+)$/iu.exec(sentence);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const items = splitPlainItems(match[2]);
  if (items.length < 2 || items.some((item) => item.length > 80 || /^\d{1,2}$/u.test(item))) {
    return null;
  }
  return { title: match[1].trim(), items };
}

function splitPlainItems(value: string): string[] {
  return value
    .replace(/[.!?]+$/u, '')
    .split(/,\s+|\s+und\s+/iu)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

const CODE_FENCE = '```';

function hasBlockMarkdown(text: string): boolean {
  return (
    /(^|\n)\s*(#{1,4} |> |\|.+\|)/.test(text) ||
    /(^|\n)\s*[-*]\s+\S/.test(text) ||
    text.split('\n').some((line) => line.trimStart().startsWith(CODE_FENCE))
  );
}

function factRow(sentence: string): [string, string] | null {
  const match =
    /^(?:Die |Der |Das )?(?:empfohlene |maximale |max\. )?(Materialstärke|Belastung(?: Zug FZ)?|Anzugsdrehmoment|Artikel(?:nummern|nummer|-Nr\.?))\b[^.]{0,80}?\b(?:beträgt|sind|ist)\s+(.+)$/iu.exec(
      sentence,
    );
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const label = /^Artikel/iu.test(match[1]) ? 'Artikelnummer' : match[1];
  return [label, match[2].replace(/[.!?]$/, '').trim()];
}

function advantageItems(sentence: string): string[] {
  const match = /^(?:Die )?Vorteile sind:\s*(.+)$/iu.exec(sentence);
  if (!match?.[1]) {
    return [];
  }
  return match[1]
    .split(/,\s+/)
    .map((item) => item.replace(/[.!?]$/, '').trim())
    .filter((item) => item.length > 2);
}

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? '';
    const trimmed = line.trim();
    if (trimmed === '') {
      index += 1;
      continue;
    }
    if (trimmed.startsWith(CODE_FENCE)) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? '').trim().startsWith(CODE_FENCE)) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }
    const heading = /^(#{1,4})\s+(.+)$/.exec(trimmed);
    if (heading?.[1] && heading[2]) {
      const marks = heading[1].length;
      const level = marks <= 1 ? 2 : marks === 2 ? 3 : 4;
      blocks.push({ type: 'h', level, text: heading[2].trim() });
      index += 1;
      continue;
    }
    if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      index += 1;
      continue;
    }
    if (trimmed.startsWith('>')) {
      const quote: string[] = [];
      while (index < lines.length && (lines[index] ?? '').trim().startsWith('>')) {
        quote.push((lines[index] ?? '').trim().replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push({ type: 'quote', text: quote.join(' ') });
      continue;
    }
    if (isTableStart(lines, index)) {
      const headers = splitRow(lines[index] ?? '');
      index += 2;
      const rows: string[][] = [];
      while (
        index < lines.length &&
        (lines[index] ?? '').includes('|') &&
        (lines[index] ?? '').trim() !== ''
      ) {
        rows.push(splitRow(lines[index] ?? ''));
        index += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }
    if (/^\s*[-*]\s+\S/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*[-*]\s+\S/.test(lines[index] ?? '')) {
        const item = /^\s*[-*]\s+(.+)$/.exec(lines[index] ?? '');
        items.push((item?.[1] ?? '').trim());
        index += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }
    if (/^\s*\d+[.)]\s+\S/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*\d+[.)]\s+\S/.test(lines[index] ?? '')) {
        const item = /^\s*\d+[.)]\s+(.+)$/.exec(lines[index] ?? '');
        items.push((item?.[1] ?? '').trim());
        index += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }
    const image = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/.exec(trimmed);
    if (image) {
      blocks.push({ type: 'img', alt: image[1] ?? '', src: image[2] ?? '' });
      index += 1;
      continue;
    }
    const paragraph: string[] = [trimmed];
    index += 1;
    while (
      index < lines.length &&
      (lines[index] ?? '').trim() !== '' &&
      !isBlockStart(lines, index)
    ) {
      paragraph.push((lines[index] ?? '').trim());
      index += 1;
    }
    blocks.push({ type: 'p', text: paragraph.join(' ') });
  }
  return blocks;
}

function isBlockStart(lines: string[], index: number): boolean {
  const trimmed = (lines[index] ?? '').trim();
  if (trimmed.startsWith(CODE_FENCE) || trimmed.startsWith('>') || /^(#{1,4})\s+\S/.test(trimmed)) {
    return true;
  }
  if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
    return true;
  }
  if (isTableStart(lines, index)) {
    return true;
  }
  return /^\s*[-*]\s+\S/.test(lines[index] ?? '') || /^\s*\d+[.)]\s+\S/.test(lines[index] ?? '');
}

function isTableStart(lines: string[], index: number): boolean {
  const header = lines[index] ?? '';
  const separator = lines[index + 1] ?? '';
  if (!header.includes('|') || !isSeparator(separator)) {
    return false;
  }
  return splitRow(header).length >= 2;
}

function isSeparator(line: string): boolean {
  if (!line.includes('-')) {
    return false;
  }
  const cells = splitRow(line);
  return cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function splitRow(line: string): string[] {
  let body = line.trim();
  if (body.startsWith('|')) {
    body = body.slice(1);
  }
  if (body.endsWith('|')) {
    body = body.slice(0, -1);
  }
  return body.split('|').map((cell) => cell.trim());
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let plain = '';
  let key = 0;
  function flush() {
    if (plain.length > 0) {
      nodes.push(plain);
      plain = '';
    }
  }
  let index = 0;
  while (index < text.length) {
    const image = matchImage(text, index);
    if (image) {
      flush();
      const src = safeHttp(image.url);
      nodes.push(
        src ? (
          <img key={key} src={src} alt={image.label} className="inline-block max-w-full" />
        ) : (
          image.label
        ),
      );
      key += 1;
      index = image.next;
      continue;
    }
    const link = matchLink(text, index);
    if (link) {
      flush();
      const href = safeLink(link.url);
      nodes.push(
        href ? (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline"
          >
            {link.label}
          </a>
        ) : (
          link.label
        ),
      );
      key += 1;
      index = link.next;
      continue;
    }
    const code = matchMarker(text, index, '`');
    if (code) {
      flush();
      nodes.push(
        <code key={key} className="rounded bg-muted px-1">
          {code.body}
        </code>,
      );
      key += 1;
      index = code.next;
      continue;
    }
    const bold = matchMarker(text, index, '**') ?? matchMarker(text, index, '__');
    if (bold) {
      flush();
      nodes.push(<strong key={key}>{bold.body}</strong>);
      key += 1;
      index = bold.next;
      continue;
    }
    const strike = matchMarker(text, index, '~~');
    if (strike) {
      flush();
      nodes.push(<del key={key}>{strike.body}</del>);
      key += 1;
      index = strike.next;
      continue;
    }
    const italic = matchMarker(text, index, '*') ?? matchMarker(text, index, '_');
    if (italic) {
      flush();
      nodes.push(<em key={key}>{italic.body}</em>);
      key += 1;
      index = italic.next;
      continue;
    }
    plain += text[index] ?? '';
    index += 1;
  }
  flush();
  return nodes;
}

function matchMarker(
  text: string,
  start: number,
  marker: string,
): { body: string; next: number } | null {
  if (!text.startsWith(marker, start)) {
    return null;
  }
  if (marker === '*' && text.startsWith('**', start)) {
    return null;
  }
  if (marker === '_' && text.startsWith('__', start)) {
    return null;
  }
  const from = start + marker.length;
  const end = text.indexOf(marker, from);
  if (end <= from) {
    return null;
  }
  const body = text.slice(from, end);
  if (body.includes('\n')) {
    return null;
  }
  return { body, next: end + marker.length };
}

function matchImage(
  text: string,
  start: number,
): { label: string; url: string; next: number } | null {
  if (!text.startsWith('![', start)) {
    return null;
  }
  const link = matchLink(text, start + 1);
  if (!link) {
    return null;
  }
  return link;
}

function matchLink(
  text: string,
  start: number,
): { label: string; url: string; next: number } | null {
  if (text[start] !== '[') {
    return null;
  }
  const labelEnd = text.indexOf(']', start + 1);
  if (labelEnd <= start + 1 || text[labelEnd + 1] !== '(') {
    return null;
  }
  const urlEnd = text.indexOf(')', labelEnd + 2);
  if (urlEnd < 0) {
    return null;
  }
  const label = text.slice(start + 1, labelEnd);
  const url = text.slice(labelEnd + 2, urlEnd).trim();
  if (label.includes('\n') || url.length === 0 || /\s/.test(url)) {
    return null;
  }
  return { label, url, next: urlEnd + 1 };
}

function safeHttp(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    return null;
  }
  return null;
}

function safeLink(url: string): string | null {
  if (/^mailto:[^\s<>]+$/i.test(url)) {
    return url;
  }
  return safeHttp(url);
}
